import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiCreditCard, FiAlertTriangle, FiPackage } from 'react-icons/fi';
import axios from 'axios';
import './dashboard.css';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
  salesToday: 0, // changed from salesThisMonth
  totalDueToPay: 0,
  totalToReceive: 0,
  lowStockProducts: [],
  loading: true,
  error: null
});


  const [profitData, setProfitData] = useState({
    totals: {
      net_profit: 0,
      gross_profit: 0,
      total_expenses: 0
    },
    loading: true,
    error: null
  });
  const [inventoryValueData, setInventoryValueData] = useState({
  total_cost_value: 0,
  total_selling_value: 0,
  potential_profit: 0,
  total_quantity: 0,
  loading: true,
  error: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          lowStockRes,
          dailySalesRes,
          dailyProfitRes,
          supplierDuesRes,
          retailerDuesRes,
          inventoryValueRes
        ] = await Promise.all([
          axios.get('/items/low-stock'),
          axios.get('/sales/stats/day'),
          axios.get('/sales/stats/daily-profit'),
          axios.get('/suppliers/payments/total-due'),
          axios.get('/retailers/payments/total-due'),
          axios.get('/items/stats/inventory-value')
        ]);

        setDashboardData({
          salesToday: dailySalesRes.data.total_sales || 0,
          totalDueToPay: supplierDuesRes.data.total_due || 0,
          totalToReceive: retailerDuesRes.data.total_due || 0,
          lowStockProducts: lowStockRes.data.data?.map(item => ({
            id: item.item_id,
            productName: item.item_name,
            quantity: item.quantity,
            supplier: item.supplier_name || 'N/A'
          })) || [],
          loading: false,
          error: null
        });

        setProfitData({
          totals: dailyProfitRes.data || {
            net_profit: 0,
            gross_profit: 0,
            total_expenses: 0
          },
          loading: false,
          error: null
        });
          setInventoryValueData({
            total_cost_value: inventoryValueRes.data.summary.total_cost_value || 0,
            total_selling_value: inventoryValueRes.data.summary.total_selling_value || 0,
            potential_profit: inventoryValueRes.data.summary.potential_profit || 0,
            total_quantity: inventoryValueRes.data.summary.total_quantity || 0,
            loading: false,
            error: null
          });

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setDashboardData(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load dashboard data'
        }));
        setProfitData(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load profit data'
        }));
        setInventoryValueData(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load inventory value'
        }));
      }
    };

    fetchData();
  }, []);



  const formatCurrency = (amount) => {
    return 'Rs.' + amount.toLocaleString('ne-NP', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (dashboardData.loading || profitData.loading || inventoryValueData.loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-status">
          <span className="dashboard-spinner" />
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (dashboardData.error || profitData.error || inventoryValueData.error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-status error">
          <FiAlertTriangle />
          <p>{dashboardData.error || profitData.error || inventoryValueData.error}</p>
        </div>
      </div>
    );
  }

  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard Overview</h1>
        <p className="dashboard-subtitle">{todayLabel}</p>
      </div>

      <div className="dashboard-grid">
        {/* Sales Today Card */}
        <div className="dashboard-card card--sales">
          <div className="card-header">
            <span className="card-icon-wrap"><FiTrendingUp className="card-icon" /></span>
            <h3>Sales Today</h3>
          </div>
          <div className="card-content">
            <p className="dashboard-card-value">{formatCurrency(dashboardData.salesToday)}</p>
            <p className="card-subtext">Total sales recorded for today</p>
          </div>
        </div>

        {/* Today's Profit/Loss Card */}
        <div className={`dashboard-card ${profitData.totals?.net_profit >= 0 ? 'card--profit' : 'card--loss'}`}>
          <div className="card-header">
            <span className="card-icon-wrap">
              {profitData.totals?.net_profit >= 0 ? (
                <FiTrendingUp className="card-icon" />
              ) : (
                <FiTrendingDown className="card-icon" />
              )}
            </span>
            <h3>{profitData.totals?.net_profit >= 0 ? "Today's Profit" : "Today's Loss"}</h3>
          </div>
          <div className="card-content">
            <p className={`dashboard-card-value ${profitData.totals?.net_profit >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(Math.abs(profitData.totals?.net_profit || 0))}
            </p>
            <div className="profit-details">
              <p className="card-subtext">Gross: {formatCurrency(profitData.totals?.gross_profit || 0)}</p>
              <p className="card-subtext">Expenses: {formatCurrency(profitData.totals?.total_expenses || 0)}</p>
            </div>
          </div>
        </div>

        {/* Supplier Dues Card */}
        <div className="dashboard-card card--payable">
          <div className="card-header">
            <span className="card-icon-wrap"><FiCreditCard className="card-icon" /></span>
            <h3>Amount to Pay</h3>
          </div>
          <div className="card-content">
            {dashboardData.totalDueToPay === 0 ? (
              <p className="dashboard-card-value cleared">Cleared</p>
            ) : dashboardData.totalDueToPay < 0 ? (
              <>
                <p className="dashboard-card-value advance">{formatCurrency(Math.abs(dashboardData.totalDueToPay))}
                  <span className="adv-label">(Adv)</span>
                </p>
                <p className="card-subtext">You have advance payment with suppliers</p>
              </>
            ) : (
              <>
                <p className="dashboard-card-value">{formatCurrency(dashboardData.totalDueToPay)}</p>
                <p className="card-subtext">Total due to suppliers</p>
              </>
            )}
          </div>
        </div>

        {/* Retailer Dues Card */}
        <div className="dashboard-card card--receivable">
          <div className="card-header">
            <span className="card-icon-wrap"><FiTrendingUp className="card-icon" /></span>
            <h3>Amount to Receive</h3>
          </div>
          <div className="card-content">
            {dashboardData.totalToReceive === 0 ? (
              <p className="dashboard-card-value cleared">Cleared</p>
            ) : dashboardData.totalToReceive < 0 ? (
              <>
                <p className="dashboard-card-value advance">{formatCurrency(Math.abs(dashboardData.totalToReceive))}
                  <span className="adv-label">(Adv)</span>
                </p>
                <p className="card-subtext">You have taken advance from retailers</p>
              </>
            ) : (
              <>
                <p className="dashboard-card-value">{formatCurrency(dashboardData.totalToReceive)}</p>
                <p className="card-subtext">From retailers</p>
              </>
            )}
          </div>
        </div>
        <div className="dashboard-card card--inventory">
          <div className="card-header">
            <span className="card-icon-wrap"><FiPackage className="card-icon" /></span>
            <h3>Inventory Value</h3>
          </div>
          <div className="card-content">
            <p className="dashboard-card-value">{formatCurrency(inventoryValueData.total_selling_value)}</p>
            <div className="profit-details">
              <p className="card-subtext">Cost value: {formatCurrency(inventoryValueData.total_cost_value)}</p>
              <p className="card-subtext">Potential profit: {formatCurrency(inventoryValueData.potential_profit)}</p>
            </div>
          </div>
        </div>

        {/* Low Stock Products Card */}
        <div className="dashboard-card card--stock full-width">
          <div className="card-header">
            <span className="card-icon-wrap"><FiAlertTriangle className="card-icon" /></span>
            <h3>Low Stock Products</h3>
          </div>
          <div className="card-content">
            {dashboardData.lowStockProducts.length > 0 ? (
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Current Stock</th>
                    <th>Supplier</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.lowStockProducts.map(product => (
                    <tr key={product.id}>
                      <td>{product.productName}</td>
                      <td>
                        <span className={`stock-badge ${
                          product.quantity === 0 ? 'out-of-stock' :
                          product.quantity < 3 ? 'critical' : 'low'
                        }`}>
                          {product.quantity}
                        </span>
                      </td>
                      <td>{product.supplier || 'N/A'}</td>
                      <td>
                        {product.quantity === 0 ? (
                          <span className="status out">Out of Stock</span>
                        ) : product.quantity < 3 ? (
                          <span className="status critical">Critical</span>
                        ) : (
                          <span className="status low">Low</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">All products are well stocked</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
